import { reaction } from "mobx";
import intl from "react-intl-universal";

import type { AppState } from "@stores/AppState";
import { createRouter, Route } from "./routing";
import { getPageTitle } from "./getPageTitle";
import { dataFromUrlName, nameFromUrlName } from "./nameToUrl";
import { PAGES, PageName, PagePathParamsMap } from "@stores/ViewStore";
import { unsavedChangesManager } from "@stores/UnsavedChangesManager";
import {PageAdditionalParams} from "../volatile/pageAdditionalParams";

const UUID_SUFFIX_REGEXP = /[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const safeDecodeURIComponent = (value: string) => {
    try {
        return decodeURIComponent(value);
    } catch {
        return value;
    }
};

const getLegacyIdFromPrettyUrlSegment = (segment: string) => (
    UUID_SUFFIX_REGEXP.test(segment) ? dataFromUrlName(segment) : undefined
);

/**
 * Router service that handles routing logic with injected GlobalState dependency
 * This eliminates circular imports while keeping routing logic separate
 */
export class RouterService {
    private router: ReturnType<typeof createRouter>;
    private previousPageId: string = '';
    private appState: AppState

    constructor(appState: AppState) {
        this.appState = appState;
        this.router = createRouter(
            this.createRoutes(),
            (queryParams) => {
                appState.rootStore.view.openPageByName(PAGES.KNOWLEDGE_BASE, {}, queryParams);
            },
        );
    }

    navigate<T extends PageName>(
        pageName: T,
        pathParams?: PagePathParamsMap[T],
        queryParams: Record<string, string> = {},
        additionalParams?: PageAdditionalParams,
        hash?: string,
    ) {
        this.appState.rootStore.view.openPageByName(
            pageName,
            pathParams,
            queryParams,
            additionalParams,
            hash,
        );
    }

    navigateByPath = (url: string, searchString: string, hashString?: string) => {
        this.router(url, searchString, hashString);
    }

    /**
     * Initialize routing - call this after GlobalState is ready
     */
    initRouting() {
        this.setupReaction();
        this.setupEventHandlers();
    }

    /**
     * Load page based on current URL
     */
    loadPageBasedOnRequestedUrl() {
        this.router(
            safeDecodeURIComponent(window.location.pathname),
            safeDecodeURIComponent(window.location.search),
            window.location.hash === '' ? undefined : safeDecodeURIComponent(window.location.hash.slice(1)),
        );
        this.appState.setContextMenu(null);
    }

    private createRoutes(): Route[] {
        const rootStore = this.appState.rootStore;

        return [
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/sign-in`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.SIGN_IN, pathParams, queryParams);
                },
                public: true,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/sign-up`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.SIGN_UP, pathParams, queryParams);
                },
                public: true,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/reset-password`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.RESET_PASSWORD, pathParams, queryParams);
                },
                public: true,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/sign-in`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.SIGN_IN, pathParams, queryParams);
                },
                public: true,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/sign-up`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.SIGN_UP, pathParams, queryParams);
                },
                public: true,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/reset-password`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.RESET_PASSWORD, pathParams, queryParams);
                },
                public: true,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/settings`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.SETTINGS_GENERAL, pathParams, queryParams);
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/settings/general`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.SETTINGS_GENERAL, pathParams, queryParams);
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/settings/members`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.SETTINGS_MEMBERS, pathParams, queryParams);
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/settings/billing`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.SETTINGS_BILLING, pathParams, queryParams);
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/settings/profile`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.SETTINGS_PROFILE, pathParams, queryParams);
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/settings/general`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.WORKSPACE_SETTINGS_GENERAL, pathParams, queryParams);
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/settings/members`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.WORKSPACE_MEMBERS, pathParams, queryParams);
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/tests`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.QUIZZES, pathParams, queryParams);
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/tests/:id`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.QUIZ_VIEW, pathParams, queryParams,
                        {
                            back: () => {
                                rootStore.view.openPageByName(
                                    PAGES.QUIZZES,
                                    pathParams,
                                    {},
                                )
                            }
                        },
                    );
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/tests/:id/edit`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.QUIZ_EDIT, pathParams, queryParams,
                        {
                            back: () => {
                                rootStore.view.openPageByName(
                                    PAGES.QUIZZES,
                                    pathParams,
                                    {},
                                )
                            }
                        },
                    );
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/tests/:id/take`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.QUIZ_TAKE, pathParams, queryParams,
                        {
                            back: () => {
                                rootStore.view.openPageByName(
                                    PAGES.QUIZZES,
                                    pathParams,
                                    {},
                                )
                            }
                        },
                    );
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/lists`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.LISTS, pathParams, queryParams);
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/task-performance`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.TASK_PERFORMANCE, pathParams, queryParams);
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/audits`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.AUDITS, pathParams, queryParams);
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/chartflow`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName('chartflow', pathParams, queryParams);
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/finance`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.FINANCE, pathParams, queryParams);
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/acknowledgments`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.ACKNOWLEDGMENTS_OVERVIEW, pathParams, queryParams);
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/libraries/:librarySlug/:docIdPretty/history`,
                action: (pathParams, queryParams, hashString) => {
                    const libraryId = getLegacyIdFromPrettyUrlSegment(pathParams.librarySlug);
                    const docId = dataFromUrlName(pathParams.docIdPretty);
                    const itemName = nameFromUrlName(pathParams.docIdPretty);
                    rootStore.view.openPageByName(
                        PAGES.DOC_HISTORY,
                        { ...pathParams, libraryId, id: docId, itemName },
                        queryParams,
                        undefined,
                        hashString
                    );
                }
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/libraries/:librarySlug/:docIdPretty`,
                action: (pathParams, queryParams, hashString) => {
                    const libraryId = getLegacyIdFromPrettyUrlSegment(pathParams.librarySlug);
                    const docId = dataFromUrlName(pathParams.docIdPretty);
                    const itemName = nameFromUrlName(pathParams.docIdPretty);
                    rootStore.view.openPageByName(
                        PAGES.DOC_EDIT,
                        { ...pathParams, libraryId, id: docId, itemName },
                        queryParams,
                        undefined,
                        hashString
                    );
                }
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/libraries/:librarySlug`,
                action: (pathParams, queryParams) => {
                    const libraryId = getLegacyIdFromPrettyUrlSegment(pathParams.librarySlug);
                    rootStore.view.openPageByName(PAGES.KNOWLEDGE_BASE, { ...pathParams, libraryId }, queryParams);
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/libraries`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.KNOWLEDGE_BASE, pathParams, queryParams);
                },
                public: false,
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/:docIdPretty/history`,
                action: (pathParams, queryParams, hashString) => {
                    const docId = dataFromUrlName(pathParams.docIdPretty);
                    const itemName = nameFromUrlName(pathParams.docIdPretty);
                    rootStore.view.openPageByName(
                        PAGES.DOC_HISTORY,
                        { ...pathParams, id: docId, itemName },
                        queryParams,
                        undefined,
                        hashString
                    );
                }
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug/:docIdPretty`,
                action: (pathParams, queryParams, hashString) => {
                    const docId = dataFromUrlName(pathParams.docIdPretty);
                    const itemName = nameFromUrlName(pathParams.docIdPretty);
                    rootStore.view.openPageByName('docEdit', { ...pathParams, id: docId, itemName }, queryParams, undefined, hashString);
                }
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team/:wsSlug`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.KNOWLEDGE_BASE, pathParams, queryParams);
                }
            },
            {
                pattern: `${import.meta.env.VITE_BASE_PATH}/:team`,
                action: (pathParams, queryParams) => {
                    rootStore.view.openPageByName(PAGES.KNOWLEDGE_BASE, pathParams, queryParams);
                }
            },
        ];
    }

    private setupReaction() {
        const rootStore = this.appState.rootStore;

        reaction(
            () => ({
                isReady: rootStore.isReady,
                isWsReady: rootStore.view.workspace?.isReady,
                url: rootStore.view.currentUrl,
                path: rootStore.view.currentPath,
                query: rootStore.view.currentQuery,
                pageId: rootStore.view.pageId,
            }),
            ({ url, path, query, pageId, isReady, isWsReady }) => {
                if (!isReady) {
                    return;
                }
                if (window.location.pathname !== path && safeDecodeURIComponent(window.location.pathname) !== path) {
                    if (pageId === this.previousPageId) {
                        window.history.replaceState(null, '', url);
                    } else {
                        window.history.pushState(null, '', url);
                    }
                } else if ((window.location.search !== query && query === '') || window.location.search !== '?' + query) {
                    window.history.replaceState(null, '', url)
                }

                if (isWsReady) {
                    document.title = getPageTitle(this.appState.userState?.dsManager, rootStore);
                }
                this.previousPageId = pageId;
            }
        );
    }

    private setupEventHandlers() {
        const rootStore = this.appState.rootStore;

        window.onpopstate = (event: PopStateEvent) => {
            const actualUrl = safeDecodeURIComponent(window.location.pathname)
                + safeDecodeURIComponent(window.location.search)
                + safeDecodeURIComponent(window.location.hash);
            if (rootStore.view.currentUrl !== actualUrl) {
                if (unsavedChangesManager.hasUnsavedChanges) {
                    const confirmLeave = confirm(intl.get('main.unsaved_changes_confirm').d("You have unsaved changes. Are you sure you want to leave this page?"));
                    if (!confirmLeave) {
                        event.preventDefault();
                        history.pushState(null, '', rootStore.view.currentUrl);
                        return;
                    } else {
                        unsavedChangesManager.clearAndLeave();
                    }
                }
                this.loadPageBasedOnRequestedUrl();
            }
        };

        window.addEventListener('hashchange', () => {
            const newHash = window.location.hash.length > 0 ? safeDecodeURIComponent(window.location.hash.substring(1)) : '';
            if (rootStore.view.hash !== newHash) {
                rootStore.view.setHash(newHash);
            }
        });
    }
}
